import apiService from './api/api.service';
import { localStorage } from '@utils/storage';
import { STORAGE_KEYS } from '@constants';
import { logger } from '@utils/logger';

class AuthService {
  async login(credentials) {
    try {
      const response = await apiService.post('/auth/login', credentials);
      
      if (response.success) {
        // API service wraps response: response.data contains the actual backend response
        const actualData = response.data.data || response.data;
        const { accessToken, refreshToken, user, dashboardAccess } = actualData;
        
        this.setTokens(accessToken, refreshToken);
        this.setUser(user);
        
        // Store dashboard access info
        if (dashboardAccess) {
          localStorage.set(STORAGE_KEYS.DASHBOARD_ACCESS, dashboardAccess);
        }
        
        // logger.info('User logged in successfully', { 
        //   userId: user?.id,
        //   canAccessDashboard: dashboardAccess?.allowed 
        // });
      }
      
      return response;
    } catch (error) {
      logger.error('Login failed', error);
      throw error;
    }
  }

  async register(userData) {
    try {
      const response = await apiService.post('/auth/register', userData);
      
      if (response.success) {
        // Don't set tokens yet - user needs to verify OTP first
        logger.info('User registered successfully, OTP sent', { 
          userId: response.data.userId 
        });
      }
      
      return response;
    } catch (error) {
      logger.error('Registration failed', error);
      throw error;
    }
  }

  async logout() {
    try {
      await apiService.post('/auth/logout');
    } catch (error) {
      logger.error('Logout API call failed', error);
    } finally {
      this.clearAuth();
      logger.info('User logged out');
    }
  }

  async refreshToken() {
    try {
      const refreshToken = this.getRefreshToken();
      
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await apiService.post('/auth/refresh', { refreshToken });
      
      if (response.success) {
        const { accessToken } = response.data;
        this.setAccessToken(accessToken);
        logger.info('Token refreshed successfully');
      }
      
      return response;
    } catch (error) {
      logger.error('Token refresh failed', error);
      this.clearAuth();
      throw error;
    }
  }

  async getCurrentUser() {
    try {
      const response = await apiService.get('/auth/me');
      
      if (response.success) {
        // Backend returns { success: true, data: { user fields } }
        // Extract the actual user data
        const userData = response.data?.data || response.data;
        this.setUser(userData);
      }
      
      return response;
    } catch (error) {
      logger.error('Failed to get current user', error);
      throw error;
    }
  }

  async forgotPassword(phone) {
    try {
      const response = await apiService.post('/auth/forgot-password', { phone });
      logger.info('Password reset OTP sent to phone', { phone });
      return response;
    } catch (error) {
      logger.error('Forgot password failed', error);
      throw error;
    }
  }

  async verifyForgotPasswordOTP(userId, otp) {
    try {
      const response = await apiService.post('/auth/verify-forgot-password-otp', { userId, otp });
      logger.info('Forgot password OTP verified');
      return response;
    } catch (error) {
      logger.error('Verify forgot password OTP failed', error);
      throw error;
    }
  }

  async resetPassword(resetToken, newPassword) {
    try {
      const response = await apiService.post('/auth/reset-password', { resetToken, newPassword });
      logger.info('Password reset successfully');
      return response;
    } catch (error) {
      logger.error('Password reset failed', error);
      throw error;
    }
  }

  setTokens(accessToken, refreshToken) {
    this.setAccessToken(accessToken);
    this.setRefreshToken(refreshToken);
  }

  setAccessToken(token) {
    localStorage.set(STORAGE_KEYS.ACCESS_TOKEN, token);
  }

  setRefreshToken(token) {
    localStorage.set(STORAGE_KEYS.REFRESH_TOKEN, token);
  }

  setUser(user) {
    localStorage.set(STORAGE_KEYS.USER, user);
  }

  setPermissions(permissions) {
    localStorage.set(STORAGE_KEYS.PERMISSIONS, permissions);
  }

  getAccessToken() {
    return localStorage.get(STORAGE_KEYS.ACCESS_TOKEN);
  }

  getRefreshToken() {
    return localStorage.get(STORAGE_KEYS.REFRESH_TOKEN);
  }

  getUser() {
    const user = localStorage.get(STORAGE_KEYS.USER);
    // console.log('AuthService.getUser() called:', user);
    return user;
  }

  getPermissions() {
    return localStorage.get(STORAGE_KEYS.PERMISSIONS) || [];
  }

  isAuthenticated() {
    return !!this.getAccessToken();
  }

  clearAuth() {
    localStorage.remove(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.remove(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.remove(STORAGE_KEYS.USER);
    localStorage.remove(STORAGE_KEYS.PERMISSIONS);
    localStorage.remove(STORAGE_KEYS.DASHBOARD_ACCESS);
  }
}

export const authService = new AuthService();
export default authService;
